import { Box } from '@mui/material';
import type * as v from 'valibot';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import type { MonetizableEnum } from '@repo/segment/enums';
import type { marketplaceEntitySchema } from '@repo/supergraph-schemas/structuredContent/hydration/MiniProductCard/schema';
import type { StructuredContentDocument } from '@repo/supergraph-schemas/structuredContent/types';
import { CtaButton } from '../../../CtaButton';
import { StackedLayout as StarRating } from '../../../StarRating/StackedLayout';
import { CtaImage } from './CtaImage';

type Props = StructuredContentDocument<{
  align?: 'left' | 'right' | 'center' | 'none';
  id: string;
  useLargeWidth?: boolean;
  omitCta?: boolean;
  omitStarRating?: boolean;
  marketplaceEntity: v.InferOutput<typeof marketplaceEntitySchema>;
}>;

export const MiniProductCard = ({ props }: Props) => {
  const {
    align = 'none',
    marketplaceEntity,
    useLargeWidth = false,
    omitCta = false,
    omitStarRating = false,
  } = props;

  if (!marketplaceEntity) return null;

  const { institution, product } = marketplaceEntity;

  const ctaAnalytics = {
    index: 0,
    ...(institution && institution.id && { institutionId: institution.id }),
    offerId: marketplaceEntity.id,
    ...(product && product.id && { productId: product.id }),
    productType: marketplaceEntity.productType,
    monetizable: marketplaceEntity.monetizable,
  };

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={marketplaceEntity.name}
      productType={marketplaceEntity.productType}
      level="offer"
      institutionId={marketplaceEntity?.institution?.id}
      institutionName={marketplaceEntity?.institution?.name}
      productId={marketplaceEntity?.product?.id}
      productName={marketplaceEntity?.product?.name}
      offerId={marketplaceEntity.id}
      offerName={marketplaceEntity.name}
      monetizable={marketplaceEntity.monetizable as MonetizableEnum}
      impressionSection="editorial_section"
      impressionPosition={0}
    >
      <div>
        <Box
          data-testid="mini_product_card"
          sx={{
            width: { md: useLargeWidth ? 225 : 160, xs: '100%' },
            maxWidth: useLargeWidth ? 225 : 160,
            flexDirection: 'column',
            gap: 1,
            marginBottom: 3,
            ...(align === 'left' && {
              display: 'block',
              float: { xs: 'unset', md: 'left' },
              marginRight: { xs: 'auto', md: 4 },
              marginLeft: { xs: 'auto', md: 0 },
            }),
            ...(align === 'right' && {
              display: 'block',
              float: { xs: 'unset', md: 'right' },
              marginRight: { xs: 'auto', md: 0 },
              marginLeft: { xs: 'auto', md: 4 },
            }),
            ...((align === 'center' || align === 'none') && {
              display: 'flex',
              justifySelf: 'center',
            }),
          }}
        >
          <CtaImage
            ctaAnalytics={ctaAnalytics}
            image={marketplaceEntity.image}
            cta={omitCta ? undefined : marketplaceEntity.cta}
            displayName={marketplaceEntity.name}
            monetizable={marketplaceEntity.monetizable}
          />
          {!omitStarRating && (
            <Box
              sx={{
                '.MuiTypography-text': {
                  fontSize: 13,
                },
                '.HelpOutlineIcon': {
                  marginTop: 0.5,
                  verticalAlign: 'center',
                },
              }}
            >
              <StarRating
                starRating={marketplaceEntity.starRating}
                starRatingTooltip={marketplaceEntity.starRatingTooltip}
                reviewLink={marketplaceEntity.reviewLink}
                hideNumber
                align="center"
              />
            </Box>
          )}
          {!omitCta && (
            <CtaButton
              cta={marketplaceEntity.cta}
              analyticsPayload={ctaAnalytics}
              federallyInsuredAnnotation={marketplaceEntity.federallyInsuredAnnotation}
            />
          )}
        </Box>
      </div>
    </AnalyticsSkuViewed>
  );
};
